import { useDisclosure } from "@mantine/hooks";
import { t } from "i18next";
import { PropsWithChildren } from "react";

import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Grid } from "#shared/view/components/Grid";
import { Stack } from "#shared/view/components/Stack";
import { Text } from "#shared/view/components/Text";
import { Collapse } from "#shared/view/designSystem/Collapse";
import { Skeleton } from "#shared/view/designSystem/Skeleton";
import { UnstyledButton } from "#shared/view/designSystem/UnstyledButton";
import { formatPrice } from "shared";

type Props = {
  title: string;
  value: number;
  color?: string;
  defaultOpened?: boolean;
  type?: "price" | "percentage";
  loading?: boolean;
};

export const CostCollapsable = ({
  children,
  title,
  value,
  color,
  defaultOpened = false,
  type = "price",
  loading = false,
  ...props
}: PropsWithChildren<Props>) => {
  const [opened, { toggle }] = useDisclosure(defaultOpened);

  return (
    <Stack gap="xs" {...props}>
      <Grid align="center" gap="0" columns={24}>
        <Grid.Col span={12}>
          <Text fz="md" fw={600}>
            {title}
          </Text>
        </Grid.Col>
        <Grid.Col span={2}>
          <Text ta="right" fz="md" fw={700} c={color} data-testid="section-cost">
            {loading ? (
              <Skeleton height={24} width={60} />
            ) : type === "price" ? (
              formatPrice(value)
            ) : (
              `${value}%`
            )}
          </Text>
        </Grid.Col>
        <Grid.Col span={10} p={0}>
          {children && (
            <Flex justify="end" align={"center"}>
              <UnstyledButton onClick={toggle} c={"dimmed"}>
                <Flex align="center">
                  <Text fz="xs" c={"dimmed"}>
                    {opened ? t("quoteRequestLine.hideDetail") : t("quoteRequestLine.showDetail")}
                  </Text>
                  {opened ? (
                    <Icon name="chevronDown" size={18} />
                  ) : (
                    <Icon name="chevronRight" size={18} />
                  )}
                </Flex>
              </UnstyledButton>
            </Flex>
          )}
        </Grid.Col>
      </Grid>
      <Collapse expanded={opened}>{children}</Collapse>
    </Stack>
  );
};
